/**
 * Auth Store
 * Manages authentication state using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    avatar?: string;
}

/**
 * JWT Token Payload structure
 */
interface TokenPayload {
    id: string;
    role: string | null;
    app_access: boolean;
    admin_access: boolean;
    exp: number;
    iat: number;
}

/**
 * Parse JWT token and extract payload
 */
function parseJwtPayload(token: string): TokenPayload | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1]!));
        return payload;
    } catch {
        return null;
    }
}

interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    appAccess: boolean;
    adminAccess: boolean;
    enforceTfa: boolean;

    // Actions
    setUser: (user: User | null) => void;
    setTokens: (accessToken: string, refreshToken: string) => void;
    setGlobals: (globals: { app_access: boolean; admin_access: boolean; enforce_tfa?: boolean }) => void;
    logout: () => void;
}

// Logout callbacks that other stores can register
const logoutCallbacks: (() => void)[] = [];

export function onLogout(callback: () => void) {
    logoutCallbacks.push(callback);
    return () => {
        const index = logoutCallbacks.indexOf(callback);
        if (index > -1) logoutCallbacks.splice(index, 1);
    };
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            appAccess: false,
            adminAccess: false,
            enforceTfa: false,

            setUser: (user) =>
                set({
                    user,
                    isAuthenticated: !!user,
                }),

            setTokens: (accessToken, refreshToken) =>
                set((state) => {
                    // Parse JWT to extract access flags
                    const payload = parseJwtPayload(accessToken);
                    return {
                        accessToken,
                        refreshToken,
                        appAccess: payload?.app_access ?? false,
                        adminAccess: payload?.admin_access ?? false,
                        // Mark as authenticated if we have tokens (will verify on next API call)
                        isAuthenticated: state.isAuthenticated || !!accessToken,
                    };
                }),

            setGlobals: (globals) =>
                set({
                    appAccess: globals.app_access,
                    adminAccess: globals.admin_access,
                    enforceTfa: globals.enforce_tfa ?? false,
                }),

            logout: () => {
                // Call all registered logout callbacks (e.g., dehydrate stores)
                logoutCallbacks.forEach(cb => cb());

                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    appAccess: false,
                    adminAccess: false,
                    enforceTfa: false,
                });
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated,
                appAccess: state.appAccess,
                adminAccess: state.adminAccess,
                enforceTfa: state.enforceTfa,
            }),
        }
    )
);
